import { ModalWindow, UIHeading2 } from '@cian/ui-kit';
import * as React from 'react';
import { useSelector } from 'react-redux';

import {
  InfoPlate,
  InfrastructureTypeRows,
  OutsideIcon,
  RateInfo,
  TransportAccessibility,
} from 'shared/components/TransportAccessibility';
import { MAX_TRANSPORT_ACCESSIBILITY_RATE } from 'shared/constants/transportAccessibility';
import { getTransportAccessibilityInfrastructure } from 'shared/selectors/newbuildingMap';
import {
  selectTransportAccessibility,
  selectTransportAccessibilitySource,
} from 'shared/selectors/transportAccessibility';
import { TTAInfrastructureType } from 'shared/types/map';
import { ITransportAccessibility } from 'shared/types/transportAccessibility';

import { getOutsideRows } from './helpers';
import { prepareRate, prepareTransportAccessibility } from '../../../TransportAccessibilityRate/helpers';

export interface ITransportAccessibilityOutsideProps {
  onTransportAccessibilityChange: (infrastructureType: TTAInfrastructureType | null) => void;
}

export const TransportAccessibilityOutside: React.FC<ITransportAccessibilityOutsideProps> = ({
  onTransportAccessibilityChange,
}) => {
  const { rate, rateDetails, title } = useSelector(selectTransportAccessibility) as ITransportAccessibility;
  const departmentLink = useSelector(selectTransportAccessibilitySource);

  const displayRate = prepareRate(rate);
  const displayMaxRate = String(MAX_TRANSPORT_ACCESSIBILITY_RATE);
  const displayTerms = React.useMemo(() => prepareTransportAccessibility(rateDetails), [rateDetails]);

  const transportAccesibilityInfrastructure = useSelector(getTransportAccessibilityInfrastructure);
  const clickableRows = React.useMemo(
    () => getOutsideRows(transportAccesibilityInfrastructure),
    [transportAccesibilityInfrastructure],
  );

  const [isModalOpen, setModal] = React.useState(false);
  const openModal = React.useCallback(() => setModal(true), []);
  const closeModal = React.useCallback(() => setModal(false), []);

  const [selectedType, setSelectedType] = React.useState(clickableRows[0]?.id);

  React.useEffect(() => {
    onTransportAccessibilityChange(selectedType as TTAInfrastructureType);

    return () => onTransportAccessibilityChange(null);
  }, [onTransportAccessibilityChange]);

  const handleRowClick = React.useCallback((infrastructureType: TTAInfrastructureType) => {
    onTransportAccessibilityChange(infrastructureType);
    setSelectedType(infrastructureType);
  }, []);

  return (
    <>
      <InfoPlate
        icon={<OutsideIcon />}
        content={<RateInfo onInfoIconClick={openModal} rate={rate} rateText={prepareRate(rate)} />}
        data-testid="TransportAccessibilityOutsideInfo"
        withHorizontalMargin
      />
      <InfrastructureTypeRows
        clickableRows={clickableRows}
        nonClickableRows={[]}
        onRowClick={handleRowClick}
        selectedItemId={selectedType}
      />
      <ModalWindow
        content={
          <TransportAccessibility
            departmentLink={departmentLink}
            maxRate={displayMaxRate}
            rate={displayRate}
            terms={displayTerms}
          />
        }
        header={<UIHeading2>{title}</UIHeading2>}
        escape
        width={759}
        onClose={closeModal}
        open={isModalOpen}
        size="M"
      />
    </>
  );
};
